import React from 'react';
import { isDefined, isEmptyString } from 'lib/typeguards';
import { useAppSelector } from 'store/hooks';

export const useSetupTrackingLinks = () => {
    const { settings } = useAppSelector(state => state.settings);

    const hasAppUrlScheme =
        isDefined(settings?.app?.appURLScheme) && !isEmptyString(settings?.app?.appURLScheme);
    const hasAppStoreUrl =
        isDefined(settings?.app?.appStoreUrl) && !isEmptyString(settings?.app?.appStoreUrl);
    const hasPlayStoreUrl =
        isDefined(settings?.app?.playStoreUrl) && !isEmptyString(settings?.app?.playStoreUrl);
    const hasAppleBundleIdentifier =
        isDefined(settings?.app?.bundleIdentifier) && !isEmptyString(settings?.app?.bundleIdentifier);
    const hasAppleTeamId =
        isDefined(settings?.app?.appleTeamId) && !isEmptyString(settings?.app?.appleTeamId);
    const hasAndroidSHAFingerprints = (settings?.app?.androidSHA256CertFingerprints?.length ?? 0) > 0;
    const hasWebDomainName =
        isDefined(settings?.web?.domainName) && !isEmptyString(settings?.web?.domainName);

    const isSetupComplete =
        hasAppUrlScheme &&
        hasAppStoreUrl &&
        hasPlayStoreUrl &&
        hasAppleBundleIdentifier &&
        hasAppleTeamId &&
        hasAndroidSHAFingerprints &&
        hasWebDomainName;

    const progress = React.useMemo(() => {
        const completed =
            (hasAppUrlScheme ? 1 : 0) +
            (hasAppStoreUrl ? 1 : 0) +
            (hasPlayStoreUrl ? 1 : 0) +
            (hasAppleBundleIdentifier ? 1 : 0) +
            (hasAppleTeamId ? 1 : 0) +
            (hasAndroidSHAFingerprints ? 1 : 0) +
            (hasWebDomainName ? 1 : 0);

        return Math.max(0.1, completed / 7);
    }, [
        hasAppUrlScheme,
        hasAppStoreUrl,
        hasPlayStoreUrl,
        hasAppleBundleIdentifier,
        hasAppleTeamId,
        hasAndroidSHAFingerprints,
        hasWebDomainName
    ]);

    const isAppLinksConfigured = React.useMemo(
        () =>
            hasAppUrlScheme &&
            hasAppStoreUrl &&
            hasPlayStoreUrl &&
            hasAppleBundleIdentifier &&
            hasAppleTeamId &&
            hasAndroidSHAFingerprints,
        [
            hasAppUrlScheme,
            hasAppStoreUrl,
            hasPlayStoreUrl,
            hasAppleTeamId,
            hasAndroidSHAFingerprints,
            hasAppleBundleIdentifier
        ]
    );

    return {
        progress,
        hasAppUrlScheme,
        hasAppStoreUrl,
        hasPlayStoreUrl,
        hasAppleBundleIdentifier,
        hasAppleTeamId,
        hasAndroidSHAFingerprints,
        hasWebDomainName,
        isSetupComplete,
        isAppLinksConfigured
    };
};
